import { useParams, Navigate, Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { COLLECTIONS } from "@/data/products";
import ProductCard from "@/components/ProductCard";
import FamilyFilter, { useFamilyFilter } from "@/components/FamilyFilter";
import { useMedia } from "@/context/MediaContext";
import { useProducts } from "@/context/ProductsContext";
import Seo from "@/components/Seo";

export default function CollectionPage() {
  const { slug } = useParams();
  const img = useMedia();
  const { products } = useProducts();
  const c = COLLECTIONS[slug];
  const items = products.filter((p) => p.collection === slug);
  const ff = useFamilyFilter(items);
  if (!c) return <Navigate to="/collections" replace />;

  return (
    <main data-testid={`collection-page-${slug}`}>
      <Seo
        title={`${c.title} — Reserve 30 Parfums`}
        description={`${c.description} ${items.map((p) => p.name).join(", ")} — parfum-strength luxury fragrances from ₹499.`}
        image={c.image}
      />
      <section className="relative h-[70vh] min-h-[460px] bg-[#111111] overflow-hidden flex items-end">
        <img src={img(`collection_${slug.replace("-", "_")}`, c.image)} alt={c.title} className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#111]/80 via-[#111]/20 to-transparent" />
        <div className="relative max-w-[1440px] mx-auto w-full px-5 md:px-10 pb-16 text-[#F7F3EE]">
          <Reveal>
            <p className="overline-label">{c.tagline}</p>
            <h1 className="font-serif-brand text-5xl sm:text-6xl lg:text-8xl font-light mt-3 leading-[0.95]">{c.title}</h1>
            <p className="mt-5 max-w-md text-sm md:text-base text-[#F7F3EE]/75 leading-relaxed">{c.description}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <Reveal className="flex flex-wrap items-center gap-6 md:gap-10 mb-12 border-b border-black/10 pb-5" data-testid="collection-switcher">
          {Object.entries(COLLECTIONS).map(([s, col]) => (
            <Link
              key={s}
              to={`/collections/${s}`}
              data-testid={`collection-switch-${s}`}
              className={`relative font-serif-brand text-2xl md:text-3xl font-light transition-colors duration-300 ${s === slug ? "text-[#111]" : "text-black/35 hover:text-black/70"}`}
            >
              {col.title}
              {s === slug && <span className="absolute left-0 -bottom-[21px] h-px w-full bg-[#BA8D53]" />}
            </Link>
          ))}
          <span className="ml-auto text-[0.62rem] uppercase tracking-[0.22em] text-black/45" data-testid="collection-count">{items.length} {items.length === 1 ? "fragrance" : "fragrances"}</span>
        </Reveal>

        {items.length === 0 ? (
          <p className="text-black/50">New compositions arriving soon.</p>
        ) : (
          <>
            <FamilyFilter {...ff} total={items.length} shown={ff.filtered.length} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {ff.filtered.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 4) * 0.08}>
                  <ProductCard p={p} />
                </Reveal>
              ))}
            </div>
            {ff.filtered.length === 0 && <p className="text-black/50" data-testid="family-filter-empty">No fragrance in this collection matches that family yet.</p>}
          </>
        )}
      </section>
    </main>
  );
}
